import { get, isNil, startCase } from 'lodash';
import * as Yup from 'yup';
import {
  ALL_FIELDS,
  FREEMIUM_CHECKBOX_TYPES,
  FREEMIUM_DROPDOWN_TYPES,
  timeZones,
} from 'shared/constants';
import {
  ContactListType,
  FieldTypes,
  FormServFieldType,
  QuestionType,
  SurveyQuestionType,
  SurveyType,
  TimeZone,
  TransactionFieldType,
  TransactionFieldsType,
  WorkFlowActionTypes,
  WorkflowActionDelayTypes,
  WorkflowActionType,
  WorkflowAuthenticationTypes,
  WorkflowConditionOperators,
  WorkflowConditionOptions,
  WorkflowConditionType,
  WorkflowConditionVariableTypes,
  WorkflowConditionVariables,
  WorkflowEventTypes,
  WorkflowSubConditionVariableOptions,
  WorkflowType,
  WorkflowTypeOptions,
  WorkflowsResponseType,
} from 'shared/types';
import {
  convertQuillHTMLToText,
  convertStringWithTagsToQuillHTML,
  getPayloadQuestionText,
} from 'shared/utils';
import {
  ConditionOperatorAndMapByQuestionType,
  ValueGetterType,
  WorkFlowActionOptionType,
  WorkFlowConditionOptionType,
  WorkflowFilterOptionTypes,
} from './types';
import { getRangeToRender } from '../response/screens/view/components/range';
import {
  DateTimeConditionValue,
  NUMBER_OF_ALLOWED_ADD_TAGS,
  NUMBER_OF_ALLOWED_CONTACT_FIELDS,
  NUMBER_OF_ALLOWED_CUSTOM_HEADERS,
  NUMBER_OF_ALLOWED_RECIPIENT,
  RANK_ORDER_OPTION_POSITION,
  WorkFlowActionOptions,
  WorkFlowConditionOperators,
  WorkFlowConditionOptions,
  WorkFlowConditionVariableTypeOptions,
  WorkFlowEventOptions,
  WorkFlowMatchOptions,
  WorkFlowTypeOptions,
} from './constants';
import i18n from '../../../i18n';

export const getWorkflowFormValues = (data: WorkflowType): WorkflowType => {
  return {
    ...data,
    actions: data.actions?.map((action) => ({
      ...action,
      action_meta: {
        ...action.action_meta,
        authentication: action?.action_meta
          ?.authentication as WorkflowAuthenticationTypes,
        subject: action?.action_meta?.subject
          ? convertStringWithTagsToQuillHTML(action?.action_meta?.subject)
          : null,
        body: action?.action_meta?.body
          ? convertStringWithTagsToQuillHTML(action?.action_meta?.body)
          : null,
        url: action?.action_meta?.url
          ? convertStringWithTagsToQuillHTML(
              action?.action_meta?.url?.replace(/ /g, ''),
            )
          : null,
        payload: action?.action_meta?.payload
          ? convertStringWithTagsToQuillHTML(action?.action_meta?.payload)
          : null,
      },
    })),
  };
};

export const getWorkflowPayload = (data: WorkflowType): WorkflowType => {
  return {
    ...data,
    actions: data.actions?.map((action) => ({
      ...action,
      action_meta: {
        ...action.action_meta,
        subject: action?.action_meta?.subject
          ? convertQuillHTMLToText(action?.action_meta?.subject)
          : null,
        body: action?.action_meta?.body
          ? getPayloadQuestionText(action?.action_meta?.body)
          : null,
        url: action?.action_meta?.url
          ? convertQuillHTMLToText(action?.action_meta?.url).replace(/ /g, '')
          : null,
        payload: action?.action_meta?.payload
          ? JSON.stringify(
              JSON.parse(
                convertQuillHTMLToText(action?.action_meta?.payload, true),
              ),
              null,
              4,
            )
          : null,
      },
    })),
  };
};

export const getConditionOperatorsByQuestion = ({
  question,
  variable_type,
}: {
  question: SurveyQuestionType;
  variable_type?: WorkflowConditionVariableTypes | null;
}): ConditionOperatorAndMapByQuestionType => {
  const range = question?.type_info?.scale_properties?.range;
  const questionType = question?.type_info?.question_type;
  const matrixType = question?.type_info?.matrix_properties?.type;
  if (questionType === 'MATRIX_RATING_SCALE') {
    return {
      operators: [
        ...(matrixType === 'SINGLE_ANSWER'
          ? (['IS', 'IS_NOT'] as WorkflowConditionOperators[])
          : []),
        'IS_EMPTY',
        'IS_NOT_EMPTY',
      ],
      operatorMap: {
        ...(matrixType === 'SINGLE_ANSWER'
          ? ({
              IS: 'DROPDOWN',
              IS_NOT: 'DROPDOWN',
            } as { [key in WorkflowConditionOperators]?: ValueGetterType })
          : {}),
        IS_EMPTY: 'NONE',
        IS_NOT_EMPTY: 'NONE',
      },
    };
  }
  if (questionType === 'RANGE' || questionType === 'BOOLEAN') {
    if (variable_type === 'RATING_GROUP') {
      return {
        operators: [
          'IS',
          'IS_NOT',
          'IS_EMPTY',
          'IS_NOT_EMPTY',
          // 'CONTAINS',
          // 'DOES_NOT_CONTAINS',
        ],
        operatorMap: {
          IS: 'DROPDOWN',
          IS_NOT: 'DROPDOWN',
          IS_EMPTY: 'NONE',
          IS_NOT_EMPTY: 'NONE',
          // CONTAINS: 'TEXT',
          // DOES_NOT_CONTAINS: 'TEXT',
        },
      };
    } else {
      return {
        operators: [
          'IS',
          'IS_NOT',
          'IS_EMPTY',
          'IS_NOT_EMPTY',
          'GREATER_THAN',
          'LESSER_THAN',
          'GREATER_THAN_OR_EQUALS',
          'LESSER_THAN_OR_EQUALS',
        ],
        operatorMap: {
          IS: 'DROPDOWN',
          IS_NOT: 'DROPDOWN',
          IS_EMPTY: 'NONE',
          IS_NOT_EMPTY: 'NONE',
          GREATER_THAN: 'DROPDOWN',
          LESSER_THAN: 'DROPDOWN',
          GREATER_THAN_OR_EQUALS: 'DROPDOWN',
          LESSER_THAN_OR_EQUALS: 'DROPDOWN',
        },
      };
    }
  }
  if (questionType === 'SLIDER') {
    return {
      operators: [
        'IS',
        'IS_NOT',
        'IS_EMPTY',
        'IS_NOT_EMPTY',
        'GREATER_THAN',
        'LESSER_THAN',
        'GREATER_THAN_OR_EQUALS',
        'LESSER_THAN_OR_EQUALS',
      ],
      operatorMap: {
        IS: 'NUMBER',
        IS_NOT: 'NUMBER',
        IS_EMPTY: 'NONE',
        IS_NOT_EMPTY: 'NONE',
        GREATER_THAN: 'NUMBER',
        LESSER_THAN: 'NUMBER',
        GREATER_THAN_OR_EQUALS: 'NUMBER',
        LESSER_THAN_OR_EQUALS: 'NUMBER',
      },
    };
  }
  if (questionType === 'RANK_ORDER') {
    return {
      operators: [
        'IS',
        'IS_NOT',
        'IS_EMPTY',
        'IS_NOT_EMPTY',
        'GREATER_THAN',
        'LESSER_THAN',
        'GREATER_THAN_OR_EQUALS',
        'LESSER_THAN_OR_EQUALS',
      ],
      operatorMap: {
        IS: 'DROPDOWN',
        IS_NOT: 'DROPDOWN',
        IS_EMPTY: 'NONE',
        IS_NOT_EMPTY: 'NONE',
        GREATER_THAN: 'DROPDOWN',
        LESSER_THAN: 'DROPDOWN',
        GREATER_THAN_OR_EQUALS: 'DROPDOWN',
        LESSER_THAN_OR_EQUALS: 'DROPDOWN',
      },
    };
  }
  if (questionType === 'DROPDOWN' || questionType === 'MULTI_SELECT') {
    return {
      operators: ['IS', 'IS_NOT', 'IS_EMPTY', 'IS_NOT_EMPTY'],
      operatorMap: {
        IS: 'DROPDOWN',
        IS_NOT: 'DROPDOWN',
        IS_EMPTY: 'NONE',
        IS_NOT_EMPTY: 'NONE',
      },
    };
  }
  if (questionType === 'CONSENT') {
    return {
      operators: ['IS', 'IS_EMPTY'],
      operatorMap: {
        IS: 'DROPDOWN',
        IS_EMPTY: 'NONE',
      },
    };
  }
  if (questionType === 'FILE') {
    return {
      operators: ['IS_EMPTY', 'IS_NOT_EMPTY'],
      operatorMap: {
        IS_EMPTY: 'NONE',
        IS_NOT_EMPTY: 'NONE',
      },
    };
  }
  if (questionType === 'TEXT' || questionType === 'FORM_FIELD') {
    return {
      operators: [
        'IS',
        'IS_NOT',
        'IS_EMPTY',
        'IS_NOT_EMPTY',
        'CONTAINS',
        'DOES_NOT_CONTAINS',
      ],
      operatorMap: {
        IS: 'TEXT',
        IS_NOT: 'TEXT',
        IS_EMPTY: 'NONE',
        IS_NOT_EMPTY: 'NONE',
        CONTAINS: 'TEXT',
        DOES_NOT_CONTAINS: 'TEXT',
      },
    };
  }
  if (questionType === 'NUMBER') {
    return {
      operators: [
        'IS',
        'IS_NOT',
        'IS_EMPTY',
        'IS_NOT_EMPTY',
        'LESSER_THAN',
        'GREATER_THAN',
        'LESSER_THAN_OR_EQUALS',
        'GREATER_THAN_OR_EQUALS',
        'BETWEEN',
      ],
      operatorMap: {
        IS: 'TEXT',
        IS_NOT: 'TEXT',
        IS_EMPTY: 'NONE',
        IS_NOT_EMPTY: 'NONE',
        LESSER_THAN: 'NUMBER',
        GREATER_THAN: 'NUMBER',
        LESSER_THAN_OR_EQUALS: 'NUMBER',
        GREATER_THAN_OR_EQUALS: 'NUMBER',
        BETWEEN: 'NUMBER_BETWEEN',
      },
    };
  }
  if (questionType === 'CONTACT_FORM') {
    return {
      operators: [
        'IS',
        'IS_NOT',
        'IS_EMPTY',
        'IS_NOT_EMPTY',
        'CONTAINS',
        'DOES_NOT_CONTAINS',
      ],
      operatorMap: {
        IS: 'TEXT',
        IS_NOT: 'TEXT',
        IS_EMPTY: 'NONE',
        IS_NOT_EMPTY: 'NONE',
        CONTAINS: 'TEXT',
        DOES_NOT_CONTAINS: 'TEXT',
      },
    };
  }
  if (questionType === 'DATE_TIME') {
    return {
      operators: [
        'IS',
        'IS_NOT',
        'IS_EMPTY',
        'IS_NOT_EMPTY',
        'BEFORE',
        'AFTER',
        'BETWEEN',
      ],
      operatorMap: {
        IS: 'DROPDOWN',
        IS_NOT: 'DROPDOWN',
        IS_EMPTY: 'NONE',
        IS_NOT_EMPTY: 'NONE',
        BEFORE: 'DATE',
        AFTER: 'DATE',
        BETWEEN: 'DATE_BETWEEN',
      },
    };
  }
  return {
    operators: [
      'IS_EMPTY',
      'IS_NOT_EMPTY',
      'CONTAINS',
      'DOES_NOT_CONTAINS',
      'STARTS_WITH',
      'ENDS_WITH',
    ],
    operatorMap: {
      IS_EMPTY: 'NONE',
      IS_NOT_EMPTY: 'NONE',
      CONTAINS: 'TEXT',
      DOES_NOT_CONTAINS: 'TEXT',
      STARTS_WITH: 'TEXT',
      ENDS_WITH: 'TEXT',
    },
  };
};

export const getConditionOperatorsByContactField = ({
  field,
}: {
  field: FormServFieldType;
}): ConditionOperatorAndMapByQuestionType => {
  if (['timezone', 'list_ids'].includes(field.name)) {
    return {
      operators: ['IS', 'IS_NOT', 'IS_EMPTY', 'IS_NOT_EMPTY'],
      operatorMap: {
        IS: 'DROPDOWN',
        IS_NOT: 'DROPDOWN',
        IS_EMPTY: 'NONE',
        IS_NOT_EMPTY: 'NONE',
      },
    };
  }
  if (
    (['text', 'phone_number', 'email', 'url'] as FieldTypes[]).includes(
      ALL_FIELDS[field?.type] as FieldTypes,
    )
  ) {
    return {
      operators: [
        'IS',
        'IS_NOT',
        'IS_EMPTY',
        'IS_NOT_EMPTY',
        'CONTAINS',
        'DOES_NOT_CONTAINS',
        'STARTS_WITH',
        'ENDS_WITH',
      ],
      operatorMap: {
        IS: 'TEXT',
        IS_NOT: 'TEXT',
        CONTAINS: 'TEXT',
        DOES_NOT_CONTAINS: 'TEXT',
        STARTS_WITH: 'TEXT',
        ENDS_WITH: 'TEXT',
        IS_EMPTY: 'NONE',
        IS_NOT_EMPTY: 'NONE',
      },
    };
  }
  if (
    (['paragraph'] as FieldTypes[]).includes(
      ALL_FIELDS[field?.type] as FieldTypes,
    )
  ) {
    return {
      operators: [
        'IS_EMPTY',
        'IS_NOT_EMPTY',
        'CONTAINS',
        'DOES_NOT_CONTAINS',
        'STARTS_WITH',
        'ENDS_WITH',
      ],
      operatorMap: {
        IS_EMPTY: 'NONE',
        IS_NOT_EMPTY: 'NONE',
        CONTAINS: 'TEXT',
        DOES_NOT_CONTAINS: 'TEXT',
        STARTS_WITH: 'TEXT',
        ENDS_WITH: 'TEXT',
      },
    };
  }
  if (
    (['number'] as FieldTypes[]).includes(ALL_FIELDS[field?.type] as FieldTypes)
  ) {
    return {
      operators: [
        'IS',
        'IS_NOT',
        'IS_EMPTY',
        'IS_NOT_EMPTY',
        'LESSER_THAN',
        'GREATER_THAN',
        'LESSER_THAN_OR_EQUALS',
        'GREATER_THAN_OR_EQUALS',
      ],
      operatorMap: {
        IS: 'NUMBER',
        IS_NOT: 'NUMBER',
        IS_EMPTY: 'NONE',
        IS_NOT_EMPTY: 'NONE',
        LESSER_THAN: 'NUMBER',
        GREATER_THAN: 'NUMBER',
        LESSER_THAN_OR_EQUALS: 'NUMBER',
        GREATER_THAN_OR_EQUALS: 'NUMBER',
      },
    };
  }
  if (
    (['dropdown'] as FieldTypes[]).includes(
      ALL_FIELDS[field?.type] as FieldTypes,
    )
  ) {
    return {
      operators: ['IS', 'IS_NOT', 'IS_EMPTY', 'IS_NOT_EMPTY'],
      operatorMap: {
        IS: 'DROPDOWN',
        IS_NOT: 'DROPDOWN',
        IS_EMPTY: 'NONE',
        IS_NOT_EMPTY: 'NONE',
      },
    };
  }
  // if (
  //   (['date'] as FieldTypes[]).includes(ALL_FIELDS[field?.type] as FieldTypes)
  // ) {
  //   return {
  //     operators: ['IS', 'IS_NOT', 'IS_EMPTY', 'IS_NOT_EMPTY'],
  //     operatorMap: {
  //       IS: 'DATE',
  //       IS_NOT: 'DATE',
  //       IS_EMPTY: 'NONE',
  //       IS_NOT_EMPTY: 'NONE',
  //     },
  //   };
  // }
  if (
    (['checkbox'] as FieldTypes[]).includes(
      ALL_FIELDS[field?.type] as FieldTypes,
    )
  ) {
    return {
      operators: ['IS', 'IS_NOT'],
      operatorMap: {
        IS: 'DROPDOWN',
        IS_NOT: 'DROPDOWN',
      },
    };
  }
  return { operatorMap: {}, operators: [] };
};

export const getContactFieldType = (field) => {
  if (
    FREEMIUM_DROPDOWN_TYPES.includes(field?.type) ||
    FREEMIUM_CHECKBOX_TYPES.includes(field?.type) ||
    ['timezone'].includes(field.name)
  ) {
    return 'DROPDOWN';
  } else if (['list_ids'].includes(field.name)) {
    return 'MULTISELECT';
  }
  return 'TEXT';
};

export const getValueOptionsByContactField = ({
  field,
  contactLists,
}: {
  field: FormServFieldType;
  contactLists: ContactListType[];
}): {
  id: string;
  label: string;
}[] => {
  if (field.name === 'timezone') {
    return timeZones.map(({ offset, timeZone }: TimeZone) => ({
      id: timeZone || '',
      label: `${timeZone} (${offset})`,
    }));
  }
  if (field.name === 'list_ids' && contactLists.length > 0) {
    return contactLists.map(({ display_id, label }: ContactListType) => ({
      id: display_id || '',
      label: label || '',
    }));
  }
  if ((ALL_FIELDS[field?.type] as FieldTypes) === 'dropdown') {
    return (field.choices || []).map((choice) => ({
      id: choice.id || '',
      label: i18n.exists(
        `contacts.import_contact.form_choices.${field.name}.${choice.id}`,
      )
        ? `${i18n.t(
            `contacts.import_contact.form_choices.${field.name}.${choice.id}`,
          )}`
        : choice.value || '',
    }));
  }
  if ((ALL_FIELDS[field?.type] as FieldTypes) === 'checkbox') {
    return [
      {
        id: 'true',
        label: i18n.t('true'),
      },
      {
        id: 'false',
        label: i18n.t('false'),
      },
    ];
  }
  return [];
};

export const getValueOptionsByQuestion = ({
  question,
  variable_type,
}: {
  question: SurveyQuestionType;
  variable_type?: WorkflowConditionVariableTypes | null;
}): {
  id: string;
  label: string;
}[] => {
  const question_type = question?.type_info?.question_type;
  if (!question_type) return [];
  if ((['RANGE', 'BOOLEAN'] as QuestionType[]).includes(question_type)) {
    if (variable_type === 'RATING_GROUP') {
      const range = question?.type_info?.scale_properties?.range;
      if (!range) return [];
      return range?.map((range) => ({
        id: range.label ? i18n.t(`rating_group.${range.label}`) : '',
        label: range.label ? i18n.t(`rating_group.${range.label}`) : '',
      }));
    } else {
      return getRangeToRender(question).map((range) => ({
        id: range.value,
        label: range.value,
      }));
    }
  }
  if (question_type === 'RANK_ORDER') {
    return (question.choices || [])
      .filter((c) => !c.meta?.is_deleted)
      .map((_, i) => ({
        id: `${i + 1}`,
        label: `${i18n.t(RANK_ORDER_OPTION_POSITION[i].value)}`,
      }));
  }
  if ((['DROPDOWN', 'MULTI_SELECT'] as QuestionType[]).includes(question_type))
    return (question.choices || [])
      .filter((choice) => !choice?.meta?.is_deleted)
      .map((choice) => ({
        id: choice.id || '',
        label: choice.value || '',
      }));
  if (question_type === 'CONSENT') {
    return question?.type_info?.meta?.display_format === 'BUTTON'
      ? (
          question?.type_info?.scale_properties?.labels?.values?.slice(0, 2) ||
          []
        ).map((value, idx) => ({
          id: `${Boolean(1 - idx)}`,
          label: value,
        }))
      : ['True', 'False'].map((value, idx) => ({
          id: `${Boolean(1 - idx)}`,
          label: value,
        }));
  }
  if (question_type === 'FORM_FIELD') {
    return (question?.type_info?.form_fields?.fields || []).map((value) => ({
      id: value.id || '',
      label: value.label || '',
    }));
  }
  if (question_type === 'CONTACT_FORM') {
    return (question?.type_info?.contact_form?.fields || []).map((value) => ({
      id: value.id || '',
      label: value.label || '',
    }));
  }
  if (question_type === 'MATRIX_RATING_SCALE') {
    return (question?.type_info?.matrix_properties?.rating_labels || [])
      .filter((rl) => rl.is_active)
      .map((rl) => ({
        id: rl.id || '',
        label: rl.text || '',
      }));
  }
  if (question_type === 'DATE_TIME') {
    return (DateTimeConditionValue || []).map((item) => ({
      id: item.id,
      label: `${i18n.t(item.label)}`,
    }));
  }
  return [];
};

export const getValueType = ({
  option,
  question,
  operator,
  field,
}: {
  operator?: WorkflowConditionOperators;
  option?: WorkFlowConditionOptionType;
  question?: SurveyQuestionType;
  field?: FormServFieldType;
}): ValueGetterType => {
  const conditionOperatorsByQuestionType = question?.id
    ? getConditionOperatorsByQuestion({
        question,
      })
    : undefined;
  if (operator && conditionOperatorsByQuestionType?.operatorMap?.[operator]) {
    return conditionOperatorsByQuestionType.operatorMap[operator] || 'NONE';
  }
  const conditionOperatorsByContactField = field?.id
    ? getConditionOperatorsByContactField({
        field,
      })
    : undefined;
  if (operator && conditionOperatorsByContactField?.operatorMap?.[operator]) {
    return conditionOperatorsByContactField.operatorMap[operator] || 'NONE';
  }
  if (operator && option?.operatorValueMap?.[operator]) {
    return option.operatorValueMap[operator] || 'NONE';
  }
  if (
    question?.type_info?.question_type &&
    (['TEXT', 'PARAGRAPH'] as QuestionType[]).includes(
      question?.type_info?.question_type,
    )
  )
    return 'TEXT';
  return 'NONE';
};

export const getWorkFlowActionOptions = ({
  event,
  actions,
  action_type,
}: {
  event?: WorkflowEventTypes | null;
  actions?: WorkflowActionType[] | null;
  action_type?: WorkFlowActionTypes | null;
}): WorkFlowActionOptionType[] => {
  const selectedActionTypes = actions
    ?.map((_, index) => {
      return actions[index]?.action_type;
    })
    .filter((val) => !!val);
  if (event === 'EVERYTIME_A_CONTACT_IS_UNSUBSCRIBED') {
    return (
      ['SEND_AN_EMAIL', 'WEBHOOK', 'UPDATE_A_CONTACT'] as WorkFlowActionTypes[]
    ).map((key) => ({
      id: key,
      label: WorkFlowActionOptions[key],
    }));
  }
  return Object.keys(WorkFlowActionOptions)
    .filter(
      (option) =>
        option === action_type ||
        ([
          'MARK_AS_FAVOURITE',
          'MARK_AS_INVALID',
          'MARK_AS_NON-ACTIONABLE',
        ].includes(option)
          ? selectedActionTypes &&
            !selectedActionTypes.includes(option as WorkFlowActionTypes)
          : true),
    )
    .map((key) => ({
      id: key as WorkFlowActionTypes,
      label: WorkFlowActionOptions[key],
    }));
};

export const getWorkflowPlaceholders = ({
  surveyData,
  transactionFields,
  allPlaceholders,
  event,
}: {
  surveyData?: SurveyType;
  transactionFields?: TransactionFieldsType;
  allPlaceholders?: Record<string, string[]>;
  event?: WorkflowEventTypes | null;
}) => {
  // populate survey specific placeholders
  const blocksData = {};
  surveyData?.blocks?.forEach((b) => {
    if (b.question_name) blocksData[b.question_name] = b.title;
    b.category?.forEach((c) => {
      if (c.question_name) blocksData[c.question_name] = b.title;
    });
  });
  const surveyBlockNames = surveyData?.question_details?.questions
    ?.filter(
      (q) =>
        !q.type_info?.meta?.is_deleted &&
        q.type_info?.question_type &&
        !WORKFLOW_DISABLED_QUESTIONS.includes(q.type_info?.question_type),
    )
    .map((q) => {
      return (
        'questions.' + blocksData[q.name]?.toLowerCase().replaceAll(' ', '_')
      );
    });
  const uniqueSurveyBlockNames = surveyBlockNames?.filter(
    (item, index) => surveyBlockNames.indexOf(item) === index,
  );

  // populate transaction specific placeholders
  const allTransactionFields = transactionFields?.fields
    ?.filter((item: TransactionFieldType) => !item.deleted)
    .map((t) => {
      return t.name?.toLowerCase().replaceAll(' ', '_');
    });

  // const surveyBlockNames = surveyData?.blocks?.map((each) => {
  //   if (each.name != 'TY' && !each.is_deleted)
  //     return 'questions.' + each.title?.toLowerCase().replaceAll(' ', '_');
  // });
  const mergedPlaceholders = uniqueSurveyBlockNames
    ? {
        ...(allPlaceholders || {}),
        ...(allTransactionFields ? { transaction: allTransactionFields } : {}),
        response: allPlaceholders?.response
          ? [...allPlaceholders.response, ...uniqueSurveyBlockNames]
          : [...uniqueSurveyBlockNames],
      }
    : allPlaceholders || {};
  const newPlaceholders = Object.keys(mergedPlaceholders || {}).map(
    (a: string) => ({
      id: a,
      label: `${i18n.t(`contacts.import_contact.placeholder_headers.${a}`)}`,
      options:
        mergedPlaceholders?.[a] &&
        mergedPlaceholders?.[a]?.map((k: string) => ({
          id: k,
          name: i18n.exists(`contacts.import_contact.default_fields.${a}.${k}`)
            ? `${i18n.t(`contacts.import_contact.default_fields.${a}.${k}`)}`
            : startCase(k),
          placeholder: `${'{{'}${a}.${k}${'}}'}`,
        })),
    }),
  );
  return (
    newPlaceholders.filter((item) =>
      event === 'EVERYTIME_A_CONTACT_IS_UNSUBSCRIBED'
        ? ['contact', 'account'].includes(item.id)
        : true,
    ) || []
  );
};

/* eslint-disable */
const urlRegex =
  /https?:\/\/(?:www\.)?([-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b)*(\/[\/\d\w\.-]*)*(?:[\?])*(.+)*/gi;

export const getWorkflowSchema = ({
  formValues,
  surveyData,
}: {
  formValues: WorkflowType;
  workflow?: WorkflowType;
  surveyData?: SurveyType;
}) => {
  const { event, type } = formValues;
  return Yup.object({
    name: Yup.string()
      .trim()
      .required(`${i18n.t('forms.err_msg.name_required')}`)
      .max(
        90,
        `${i18n.t('forms.err_msg.max_char', {
          len: '50',
        })}`,
      )
      .min(
        3,
        `${i18n.t('forms.err_msg.min_char', {
          len: '3',
        })}`,
      ),
    survey_id:
      event === 'EVERYTIME_A_SPECIFIC_SURVEY_IS_RESPONDED'
        ? Yup.string()
            .trim()
            .typeError(`${i18n.t('forms.err_msg.required_field')}`)
            .required(`${i18n.t('forms.err_msg.required_field')}`)
        : Yup.string().notRequired().nullable(),
    type: Yup.string()
      .trim()
      .oneOf(Object.keys(WorkFlowTypeOptions) as WorkflowTypeOptions[])
      .required(`${i18n.t('forms.err_msg.required_field')}`),
    event:
      type === 'MANUAL'
        ? Yup.string().notRequired().nullable()
        : Yup.string()
            .trim()
            .oneOf(Object.keys(WorkFlowEventOptions) as WorkflowEventTypes[])
            .required(`${i18n.t('forms.err_msg.required_field')}`),
    condition_groups_operator:
      type === 'MANUAL' || event === 'EVERYTIME_A_CONTACT_IS_UNSUBSCRIBED'
        ? Yup.string().notRequired().nullable()
        : Yup.string()
            .trim()
            .oneOf(
              Object.keys(WorkFlowMatchOptions) as WorkflowConditionOptions[],
            )
            .required(`${i18n.t('forms.err_msg.required_field')}`),
    condition_groups:
      type === 'MANUAL' || event === 'EVERYTIME_A_CONTACT_IS_UNSUBSCRIBED'
        ? Yup.array().notRequired().nullable()
        : Yup.array()
            .of(
              Yup.object({
                conditions_operator: Yup.string()
                  .trim()
                  .oneOf(
                    Object.keys(
                      WorkFlowMatchOptions,
                    ) as WorkflowConditionOptions[],
                  )
                  .required(`${i18n.t('forms.err_msg.required_field')}`),
                conditions: Yup.array()
                  .of(
                    Yup.object({
                      operator: Yup.string()
                        .trim()
                        .oneOf(
                          Object.keys(
                            WorkFlowConditionOperators,
                          ) as WorkflowConditionOperators[],
                        )
                        .typeError(`${i18n.t('forms.err_msg.required_field')}`)
                        .required(`${i18n.t('forms.err_msg.required_field')}`),
                      variable: Yup.string()
                        .trim()
                        .oneOf(
                          WorkFlowConditionOptions.map(
                            (o) => o.id,
                          ) as WorkflowConditionVariables[],
                        )
                        .typeError(`${i18n.t('forms.err_msg.required_field')}`)
                        .required(`${i18n.t('forms.err_msg.required_field')}`),
                      value: Yup.mixed().test(function (value, context) {
                        const {
                          createError,
                          parent,
                        }: Omit<Yup.TestContext, 'parent'> & {
                          parent: WorkflowConditionType;
                        } = context || {};
                        if (
                          (parent.sub_condition || []).length > 0 ||
                          parent.operator === 'IS_EMPTY' ||
                          parent.operator === 'IS_NOT_EMPTY'
                        )
                          return true;
                        if (!value)
                          return createError({
                            message: `${i18n.t(
                              'forms.err_msg.required_field',
                            )}`,
                          });
                        const selectedQuestion =
                          surveyData?.question_details?.questions?.find(
                            (q) => q.id === parent.variable_id,
                          );
                        if (
                          selectedQuestion?.type_info?.question_type ===
                          'SLIDER'
                        ) {
                          const conditionValue = Number(value);
                          const sliderData =
                            selectedQuestion?.type_info?.meta?.slider;
                          if (
                            conditionValue > (sliderData?.max || 1000) ||
                            conditionValue < (sliderData?.min || 0)
                          ) {
                            return createError({
                              message: `${i18n.t('forms.err_msg.slider_range', {
                                min: sliderData?.min || 0,
                                max: sliderData?.max || 1000,
                              })}`,
                            });
                          }
                        }
                        return true;
                      }),
                      variable_id: Yup.string()
                        .nullable()
                        .trim()
                        .test(function (value, context) {
                          const {
                            createError,
                            parent,
                          }: Omit<Yup.TestContext, 'parent'> & {
                            parent: WorkflowConditionType;
                          } = context || {};
                          if (!value && parent.variable === 'QUESTION')
                            return createError({
                              message: `${i18n.t(
                                'forms.err_msg.required_field',
                              )}`,
                            });
                          const selectedQuestion =
                            surveyData?.question_details?.questions?.find(
                              (q) => q.id === parent.variable_id,
                            );
                          if (
                            parent?.variable == 'QUESTION' &&
                            isNil(selectedQuestion)
                          ) {
                            return createError({
                              message: `${i18n.t(
                                'forms.err_msg.required_field',
                              )}`,
                            });
                          }
                          return true;
                        }),
                      variable_type: Yup.string()
                        .nullable()
                        .trim()
                        .oneOf([
                          null,
                          ...(WorkFlowConditionVariableTypeOptions.map(
                            (o) => o.id,
                          ) as WorkflowConditionVariableTypes[]),
                        ])
                        .test(function (value, context) {
                          const {
                            createError,
                            parent,
                          }: Omit<Yup.TestContext, 'parent'> & {
                            parent: WorkflowConditionType;
                          } = context || {};
                          const selectedQuestion =
                            surveyData?.question_details?.questions?.find(
                              (q) => q.id === parent.variable_id,
                            );
                          const question_type =
                            selectedQuestion?.type_info?.question_type;
                          if (
                            question_type &&
                            (['RANGE', 'BOOLEAN'] as QuestionType[]).includes(
                              question_type,
                            ) &&
                            !value &&
                            (parent.variable === 'QUESTION' ||
                              parent.variable === 'SURVEY_ID')
                          )
                            return createError({
                              message: `${i18n.t(
                                'forms.err_msg.required_field',
                              )}`,
                            });
                          return true;
                        }),
                      sub_condition: Yup.array()
                        .of(
                          Yup.object({
                            variable: Yup.string()
                              .trim()
                              .oneOf([
                                'STATEMENT',
                                'FORM_FIELD',
                                'CONTACT_FORM',
                              ] as WorkflowSubConditionVariableOptions[])
                              .required(
                                `${i18n.t('forms.err_msg.required_field')}`,
                              ),
                            variable_id: Yup.string()
                              .trim()
                              .required(
                                `${i18n.t('forms.err_msg.required_field')}`,
                              )
                              .typeError(
                                `${i18n.t('forms.err_msg.required_field')}`,
                              ),
                            deleted: Yup.boolean().nullable(),
                            value: Yup.array()
                              .of(Yup.string().trim())
                              .nullable()
                              .test(function (value, context) {
                                const { createError, path }: Yup.TestContext =
                                  context || {};
                                const condition: WorkflowConditionType = get(
                                  formValues,
                                  path.split('.sub_condition')[0],
                                );
                                if (
                                  condition.operator === 'IS_EMPTY' ||
                                  condition.operator === 'IS_NOT_EMPTY'
                                )
                                  return true;
                                if (!value || value.length === 0)
                                  return createError({
                                    message: `${i18n.t(
                                      'forms.err_msg.required_field',
                                    )}`,
                                  });
                                return true;
                              }),
                          }),
                        )
                        .nullable()
                        .typeError(`${i18n.t('forms.err_msg.required_field')}`),
                    }),
                  )
                  .max(
                    10,
                    `${i18n.t('forms.err_msg.max_limit', {
                      limit: '10',
                    })}`,
                  )
                  .nullable(),
              }),
            )
            .max(
              5,
              `${i18n.t('forms.err_msg.max_limit', {
                limit: '5',
              })}`,
            ),
    actions: Yup.array()
      .of(
        Yup.object({
          action_type: Yup.string()
            .trim()
            .oneOf(Object.keys(WorkFlowActionOptions) as WorkFlowActionTypes[])
            .required(`${i18n.t('forms.err_msg.required_field')}`),
          action_meta: Yup.object({
            authentication: getActionMetaSchema({
              formValues,
              path: '.action_meta.authentication',
              when: (act) => act?.action_type === 'WEBHOOK',
            }),
            api_key: Yup.object({
              key: getActionMetaSchema({
                formValues,
                path: '.action_meta.api_key.key',
                when: (act) =>
                  act?.action_type === 'WEBHOOK' &&
                  act?.action_meta?.authentication === 'api_key',
              }),
              value: getActionMetaSchema({
                formValues,
                path: '.action_meta.api_key.value',
                when: (act) =>
                  act?.action_type === 'WEBHOOK' &&
                  act?.action_meta?.authentication === 'api_key',
              }),
            }).nullable(),
            basic_auth: Yup.object({
              user: getActionMetaSchema({
                formValues,
                path: '.action_meta.basic_auth.user',
                when: (act) =>
                  act?.action_type === 'WEBHOOK' &&
                  act?.action_meta?.authentication === 'basic_auth',
              }),
              pass: getActionMetaSchema({
                formValues,
                path: '.action_meta.basic_auth.pass',
                when: (act) =>
                  act?.action_type === 'WEBHOOK' &&
                  act?.action_meta?.authentication === 'basic_auth',
              }),
            }).nullable(),
            custom_headers: Yup.array()
              .nullable()
              .of(
                Yup.object({
                  key: Yup.string()
                    .trim()
                    .required(`${i18n.t('forms.err_msg.required_field')}`),
                  value: Yup.string()
                    .trim()
                    .required(`${i18n.t('forms.err_msg.required_field')}`),
                }),
              )
              .max(
                NUMBER_OF_ALLOWED_CUSTOM_HEADERS,
                `${i18n.t('forms.err_msg.max_limit', {
                  limit: NUMBER_OF_ALLOWED_CUSTOM_HEADERS,
                })}`,
              ),
            payload: Yup.string()
              .trim()
              .nullable()
              .test(function (payload, context) {
                const { createError, path: p }: Yup.TestContext = context || {};
                const action: WorkflowActionType = get(
                  formValues,
                  p.replace('.action_meta.payload', ''),
                );
                if (action?.action_type && action?.action_type !== 'WEBHOOK')
                  return true;
                if (!payload)
                  return createError({
                    message: `${i18n.t('forms.err_msg.required_field')}`,
                  });
                return true;
              }),
            url: getActionMetaSchema({
              formValues,
              path: '.action_meta.url',
              when: (act) => act?.action_type === 'WEBHOOK',
            }).matches(urlRegex, `${i18n.t('forms.err_msg.valid_url')}`),
            from_name: Yup.string().trim().nullable(),
            from_mail: Yup.string()
              .trim()
              .test(function (from_name, context) {
                const { createError, path: p }: Yup.TestContext = context || {};
                const action: WorkflowActionType = get(
                  formValues,
                  p.replace('.action_meta.from_mail', ''),
                );
                if (
                  action?.action_type &&
                  !(
                    [
                      'SEND_AN_EMAIL',
                      'SEND_AN_EMAIL_TO_RESPONDENT',
                    ] as WorkFlowActionTypes[]
                  ).includes(action?.action_type)
                )
                  return true;
                if (!from_name)
                  return createError({
                    message: `${i18n.t('forms.err_msg.required_field')}`,
                  });
                return true;
              })
              .email(`${i18n.t('forms.err_msg.valid_email')}`),
            reply_to: Yup.string()
              .trim()
              .email(`${i18n.t('forms.err_msg.valid_email')}`)
              .nullable(),
            to_mail: Yup.array()
              .of(
                Yup.string()
                  .trim()
                  .email(`${i18n.t('forms.err_msg.valid_email')}`),
              )
              .nullable()
              .test(function (to_mail, context) {
                const { createError, path: p }: Yup.TestContext = context || {};
                const action: WorkflowActionType = get(
                  formValues,
                  p.replace('.action_meta.to_mail', ''),
                );
                if (
                  action?.action_type &&
                  action?.action_type !== 'SEND_AN_EMAIL'
                )
                  return true;
                if (!to_mail)
                  return createError({
                    message: `${i18n.t('forms.err_msg.required_field')}`,
                  });
                if (to_mail.length === 0) {
                  return createError({
                    message: `${i18n.t('forms.err_msg.required_field')}`,
                  });
                }
                if (to_mail.length > NUMBER_OF_ALLOWED_RECIPIENT)
                  return createError({
                    message: `${i18n.t('forms.err_msg.max_limit', {
                      len: NUMBER_OF_ALLOWED_RECIPIENT,
                    })}`,
                  });
                return true;
              }),
            subject: Yup.string()
              .trim()
              .nullable()
              .test(function (text, context) {
                const { createError, path }: Yup.TestContext = context || {};
                const action: WorkflowActionType = get(
                  formValues,
                  path.replace('.action_meta.subject', ''),
                );
                if (
                  action?.action_type &&
                  !(
                    [
                      'SEND_AN_EMAIL',
                      'SEND_AN_EMAIL_TO_RESPONDENT',
                    ] as WorkFlowActionTypes[]
                  ).includes(action?.action_type)
                )
                  return true;
                if (!text)
                  return createError({
                    message: `${i18n.t('forms.err_msg.required_field')}`,
                  });
                const questionText = convertQuillHTMLToText(text || '');
                if (questionText.length === 0) {
                  return createError({
                    message: `${i18n.t('forms.err_msg.required_field')}`,
                  });
                }
                if (questionText.length < 3)
                  return createError({
                    message: `${i18n.t('forms.err_msg.min_char', {
                      len: '3',
                    })}`,
                  });
                if (questionText.length > 230)
                  return createError({
                    message: `${i18n.t('forms.err_msg.max_char', {
                      len: '230',
                    })}`,
                  });
                return true;
              }),
            body: Yup.string()
              .trim()
              .nullable()
              .test(function (text, context) {
                const { createError, path }: Yup.TestContext = context || {};
                const action: WorkflowActionType = get(
                  formValues,
                  path.replace('.action_meta.body', ''),
                );
                if (
                  action?.action_type &&
                  !(
                    [
                      'SEND_AN_EMAIL',
                      'SEND_AN_EMAIL_TO_RESPONDENT',
                    ] as WorkFlowActionTypes[]
                  ).includes(action?.action_type)
                )
                  return true;
                if (!text)
                  return createError({
                    message: `${i18n.t('forms.err_msg.required_field')}`,
                  });
                const questionText = convertQuillHTMLToText(text || '');
                if (questionText.length === 0) {
                  return createError({
                    message: `${i18n.t('forms.err_msg.required_field')}`,
                  });
                }
                if (questionText.length < 3)
                  return createError({
                    message: `${i18n.t('forms.err_msg.min_char', {
                      len: '3',
                    })}`,
                  });
                if (questionText.length > 5000)
                  return createError({
                    message: `${i18n.t('forms.err_msg.max_char', {
                      len: '5000',
                    })}`,
                  });
                return true;
              }),
            delay: Yup.number()
              .nullable()
              .transform((num) =>
                isNaN(num) || isNil(num) ? null : Number(`${num}`),
              )
              .test(function (num, context) {
                const { createError, path }: Yup.TestContext = context || {};
                const action: WorkflowActionType = get(
                  formValues,
                  path.replace('.action_meta.body', ''),
                );
                if (
                  action?.action_type &&
                  !(
                    [
                      'SEND_AN_EMAIL',
                      'SEND_AN_EMAIL_TO_RESPONDENT',
                    ] as WorkFlowActionTypes[]
                  ).includes(action?.action_type)
                )
                  return true;
                if (num && num > 365)
                  return createError({
                    message: `${i18n.t('forms.err_msg.max_len', {
                      len: '365',
                    })}`,
                  });
                return true;
              }),
            delay_type: Yup.string()
              .trim()
              .oneOf([
                'Days',
                'Hours',
                'Minutes',
              ] as WorkflowActionDelayTypes[]),
            contact_fields: Yup.array()
              .of(
                Yup.object({
                  field: Yup.string()
                    .trim()
                    .required(`${i18n.t('forms.err_msg.required_field')}`),
                  value: Yup.string()
                    .trim()
                    .required(`${i18n.t('forms.err_msg.required_field')}`),
                }),
              )
              .nullable()
              .min(1, `${i18n.t('forms.err_msg.min_limit')}`)
              .max(
                NUMBER_OF_ALLOWED_CONTACT_FIELDS,
                `${i18n.t('forms.err_msg.max_limit', {
                  limit: NUMBER_OF_ALLOWED_CONTACT_FIELDS,
                })}`,
              ),
            tags: Yup.array()
              .of(Yup.string().trim())
              .nullable()
              .test(function (tags, context) {
                const { createError, path }: Yup.TestContext = context || {};
                const action: WorkflowActionType = get(
                  formValues,
                  path.replace('.action_meta.tags', ''),
                );
                if (action?.action_type && action?.action_type !== 'ADD_TAGS')
                  return true;
                if (!tags)
                  return createError({
                    message: `${i18n.t('forms.err_msg.required_field')}`,
                  });
                if (tags.length === 0) {
                  return createError({
                    message: `${i18n.t('forms.err_msg.required_field')}`,
                  });
                }
                if (tags.length > NUMBER_OF_ALLOWED_ADD_TAGS)
                  return createError({
                    message: `${i18n.t('forms.err_msg.max_limit', {
                      len: NUMBER_OF_ALLOWED_ADD_TAGS,
                    })}`,
                  });
                return true;
              }),
          }),
        }),
      )
      .typeError(`${i18n.t('forms.err_msg.required_field')}`),
  }).required();
};

const getActionMetaSchema = ({
  path,
  formValues,
  when,
}: {
  path: string;
  formValues: WorkflowType;
  when: (data: WorkflowActionType) => boolean;
}) =>
  Yup.string()
    .trim()
    .nullable()
    .test(function (value, context) {
      const { createError, path: p }: Yup.TestContext = context || {};
      const action: WorkflowActionType = get(formValues, p.replace(path, ''));
      if (!when(action)) return true;
      if (!value)
        return createError({
          message: `${i18n.t('forms.err_msg.required_field')}`,
        });
      return true;
    });

export const getStatusFilter = ({
  status,
  workflow,
}: {
  status: WorkflowFilterOptionTypes;
  workflow: WorkflowType;
}): boolean => {
  switch (status) {
    case 'ALL':
      return true;
    case 'ACTIVE':
      return !!workflow.active;
    case 'INACTIVE':
      return !workflow.active;
  }
};

export const getWorkflowTypeFilter = ({
  type,
  workflow,
}: {
  type: WorkflowTypeOptions;
  workflow: WorkflowType;
}): boolean => workflow.type === type;

export const getWorkflowFilterCount = ({
  filterType,
  workflowsData,
  workflowType,
}: {
  filterType: WorkflowFilterOptionTypes;
  workflowsData: WorkflowsResponseType;
  workflowType: WorkflowTypeOptions;
}): number => {
  if (filterType === 'ALL')
    return (
      (workflowsData?.workflows || []).filter((wf) => wf.type === workflowType)
        .length || 0
    );
  if (filterType === 'ACTIVE')
    return (
      (workflowsData?.workflows || []).filter(
        (wf) => wf.active && wf.type === workflowType,
      ).length || 0
    );
  if (filterType === 'INACTIVE')
    return (
      (workflowsData?.workflows || []).filter(
        (wf) => !wf.active && wf.type === workflowType,
      ).length || 0
    );
  return 0;
};

export const WORKFLOW_DISABLED_QUESTIONS = ['MESSAGE', 'THANK_YOU'];
